import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { validateWebsiteURL } from '../../../../../Common/Misc/helpers/formatting/url.helper';
import { HandleTypeEnum } from '../../../../../api/targeting/enum/handle-type.enum';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../../Common/Forms/models/control-value-accessor.model';
import { validateEmail } from '../../../../../Common/Misc/helpers/formatting/email.helper';
import { HandleModel } from '../../../../../Discover/Targeting/models/handle.model';
import { TextInputComponent } from '../../../../../Common/Forms/components/TextInput/text-input.component';
import { FocusHelper } from '../../../../../Common/Forms/helpers/focus.helper';

// Supply [ngModel] to this component, NOT [(ngModel)]
// and rely on onAdd and onRemove to process changes to handles
@Component({
    selector: 'agpr-handles-chips',
    templateUrl: './handles-chips.component.html',
    styleUrls: ['./handles-chips.component.scss'],
    providers: [getProvider(HandlesChipsComponent)],
    standalone: false
})
export class HandlesChipsComponent extends ControlValueAccessorModel<HandleModel[]> implements OnInit {

    readonly buttonTypes = ButtonTypesEnum;
    readonly handleTypeEnum = HandleTypeEnum;
    readonly FACEBOOK_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?facebook\.com\/([^\/\?\&]*).*/;
    readonly TWITTER_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?(?:twitter|x)\.com\/([^\/\?\&]*).*/;
    readonly INSTAGRAM_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?instagram\.com\/([^\/\?\&]*).*/;
    readonly LINKEDIN_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?linkedin\.com\/in\/([^\/\?\&]*).*/;
    readonly BLUESKY_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?bsky\.app\/profile\/([^\/\?\&]*).*/;
    readonly TIKTOK_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?tiktok\.com\/@([^\/\?\&]*).*/;
    readonly YOUTUBE_PAGE_FORMAT = /http[s]?:\/\/(?:www\.)?youtube\.com\/@([^\/\?\&]*).*/;

    readonly VALID_URL = /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
    readonly AT_HANDLE_FORMAT = /(?:^@?)([\w\.-]+)$/;
    readonly PHONE_NUMBER_FORMAT = /^([-+.\(\) \d]*)$/;

    // text-input passthrough stuff
    @Input() id: string;
    @Input() disabled: boolean;
    @Input() placeholder: string;
    @Input() hint: string;
    @Input() maxLength: number = 254;
    @Input() label: string;
    @Input() required: boolean = false;

    @Input() handleType: HandleTypeEnum;
    @Input() validationFunction: Function;
    @Input() tooltip: boolean = true;
    @Input() errorMessage: string = 'Invalid format';
    @Input() addPrivateData: boolean = false;
    @Input() ariaDescribedBy: string = '';
    @Input() addButtonTooltip: string;

    @Output() add: EventEmitter<HandleModel> = new EventEmitter();
    @Output() remove: EventEmitter<HandleModel> = new EventEmitter<HandleModel>();
    @Output() handlesChanged: EventEmitter<HandleModel> = new EventEmitter<HandleModel>();

    @ViewChild('textInput') textInput: TextInputComponent;
    @ViewChild('chipList') chipList: ElementRef;

    newChipText: string;
    showError: boolean = false;
    hasPreferredEmail: boolean = false;

    private allOtherHandles: HandleModel[] = [];
    private sanitizingRegex = [];

    constructor() {
        super();
    }

    ngOnInit() {
        const validate: Function = (text: string) => {
            let condition: boolean = false;
            for (const regex of this.sanitizingRegex) {
                condition = condition || regex.test(text);
            }

            return condition;
        };

        // Note : will not react to handleType changing after construction
        switch (this.handleType) {
            case HandleTypeEnum.EMAIL :
                this.validationFunction = validateEmail;
                break;
            case HandleTypeEnum.FACEBOOK:
                this.sanitizingRegex = [this.FACEBOOK_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.TWITTER:
                this.sanitizingRegex = [this.TWITTER_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.INSTAGRAM:
                this.sanitizingRegex = [this.INSTAGRAM_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.LINKEDIN:
                this.sanitizingRegex = [this.LINKEDIN_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.BLUESKY:
                this.sanitizingRegex = [this.BLUESKY_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.TIKTOK:
                this.sanitizingRegex = [this.TIKTOK_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.YOUTUBE:
                this.sanitizingRegex = [this.YOUTUBE_PAGE_FORMAT, this.AT_HANDLE_FORMAT];
                this.validationFunction = validate;
                break;
            case HandleTypeEnum.WEBSITE:
                this.validationFunction = (url: string) => {
                    return validateWebsiteURL(url);
                };
                break;
            case HandleTypeEnum.PHONE:
                this.sanitizingRegex = [this.PHONE_NUMBER_FORMAT];
                this.validationFunction = validate;
                break;
        }
    }

    // Override to only handle one type at a time
    writeValue(value: HandleModel[]): void {
        this.hasPreferredEmail = false;

        if (value) {
            this.value = value.filter(x => x.handleType === this.handleType);
            this.allOtherHandles = value.filter(x => x.handleType !== this.handleType);
            this.sortHandles();
            if (this.handleType === HandleTypeEnum.EMAIL && !!this.value.find(x => x.preferred)) {
                this.hasPreferredEmail = true;
            }
        }
    }

    registerOnChange(fn): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn): void {
        this.onModelTouched = fn;
    }

    setPrimary(handle: HandleModel) {
        handle.primary = true;
        this.value.filter(x => x !== handle).forEach(x => x.primary = false);
        this.sortAndEmitChanges();
    }

    setPreferred(handle: HandleModel) {
        if (handle.primary) {
            this.hasPreferredEmail = false;
        } else {
            this.hasPreferredEmail = true;
        }

        this.value.forEach( email => {
            email.preferred = email === handle;
            if (email.primary) {
                email.preferred = false;
            }
        });
        this.sortAndEmitChanges(handle);
    }

    private sortAndEmitChanges(handle?: HandleModel) {
        this.sortHandles();
        this.handlesChanged.emit(handle);
    }

    sortHandles() {
        const sortedArray = [];
        let primary = null;
        let preferred = null;

        this.value.forEach(chip => {
            if (chip.primary) {
                primary = chip;
            } else if (chip.preferred) {
                preferred = chip;
            } else {
                sortedArray.push(chip);
            }
        });
        if (primary) {
            sortedArray.unshift(primary);
        } else if (this.value.length > 0) {
            this.value[0].primary = true;
        }
        if (preferred) {
            sortedArray.unshift(preferred);
        }
        this.value = sortedArray;
        this.onModelChange([ ...this.value, ...this.allOtherHandles ]);
    }

    removeChip(index: number): void {
        const handle = this.value[index];
        this.value.splice(index, 1);
        this.remove.emit(handle);
        this.sortHandles();
        if (this.value.length === 0) {
            this.focusInput();
        } else {
            FocusHelper.handleFocusAfterChipRemoval(this.chipList.nativeElement, index, '.agpr-handlesChips-label');
        }
    }

    isDuplicate(handle: HandleModel): boolean {
        return !!this.value.find(_handle => _handle.value === handle.value);
    }

    handleTextInputKeyUp(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            return;
        }

        this.tryAddChip();
    }

    tryAddChip(): void {
        this.showError = false;
        if (this.newChipText) {
            this.newChipText = this.newChipText.trim();
        }

        if (!this.newChipText || this.newChipText.length === 0) {
            return;
        }

        // remove handle or url
        let cleanText: string;
        for (const regex of this.sanitizingRegex) {
            cleanText = this.newChipText.replace(regex, '$1');
            if (cleanText !== this.newChipText) {
                break;
            }
        }

        const handle: HandleModel = new HandleModel({
            handleType: this.handleType,
            value: cleanText || this.newChipText,
            primary: (this.value.length === 0)
        });

        if (this.isDuplicate(handle)) {
            return;
        }

        if (this.validationFunction && !this.validationFunction(this.newChipText)) {
            this.showError = true;
            return;
        }

        // Presumably if the user adds an extra e-mail it is with the intent to use it as preferred
        if (this.handleType === HandleTypeEnum.EMAIL && !this.hasPreferredEmail && this.addPrivateData) {
            handle.preferred = true;
            this.hasPreferredEmail = true;
        }

        this.value.push(handle);
        this.sortAndEmitChanges();
        this.add.emit(handle);
        this.textInput.clearInput();
        this.newChipText = null;
        this.showError = false;
    }

    focusInput() {
        requestAnimationFrame(() => this.textInput.input.nativeElement.focus());
    }
}
